import React, { useState } from "react";
import {
  Box,
  Typography,
  Chip,
  Switch,
  Button,
  Paper,
  FormControlLabel,
  Alert,
  Snackbar,
  CircularProgress,
  Fade,
  Divider
} from "@mui/material";
import {
  CheckCircle as AvailableIcon,
  AttachMoney as SoldIcon,
  Save as SaveIcon
} from "@mui/icons-material";
import { PropertyListing } from "../../../types/Property";
import { updatePropertyData } from "../../../services/propertyService";

interface EditPropertyStatusProps {
  selectedProperty: PropertyListing | null;
}

interface EditPropertyStatusState {
  propertyId: number;
  PropertyModuleState: number;
  SoldByPublisher: boolean;
}

export const EditPropertyStatus: React.FC<EditPropertyStatusProps> = ({
  selectedProperty,
}) => {
  const [selectedState, setSelectedState] = useState<number>(selectedProperty?.moduleStateId || 1);
  const [soldByPublisher, setSoldByPublisher] = useState<boolean>(true);
  const [loading, setLoading] = useState<boolean>(false);
  const [snackbarOpen, setSnackbarOpen] = useState<boolean>(false);
  const [snackbarMessage, setSnackbarMessage] = useState<string>("");
  const [snackbarSeverity, setSnackbarSeverity] = useState<"success" | "error">("success");


  const isRental = selectedProperty?.modeDescription === "Alquiler";
  
  const availableLabel = "Disponible";
  const unavailableLabel = isRental ? "Alquilada" : "Vendida";
  
  const handleStateChange = (newState: number) => {
    setSelectedState(newState);
    if (newState === 1) {
      setSoldByPublisher(false);
    }
  };

  const handleSoldByPublisherChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSoldByPublisher(event.target.checked);
  };

  const handleSave = async () => {
    if (!selectedProperty) return;

    setLoading(true);
    try {
      const newState: EditPropertyStatusState = {
        propertyId: selectedProperty.id_Property,
        PropertyModuleState: selectedState,
        SoldByPublisher: selectedState === 2 || selectedState === 3 ? soldByPublisher : false
      };

      const formData = new FormData();
      formData.append("PropertyId", newState.propertyId.toString());
      formData.append("PropertyModuleState", newState.PropertyModuleState.toString());
      formData.append("SoldByPublisher", newState.SoldByPublisher.toString());

      const response = await updatePropertyData("status", formData);

      if (response?.data) {
        setSnackbarMessage("Estado de la propiedad actualizado exitosamente");
        setSnackbarSeverity("success");
        setSnackbarOpen(true);
      }
    } catch (error) {
      setSnackbarMessage("Error al actualizar el estado de la propiedad");
      setSnackbarSeverity("error");
      setSnackbarOpen(true);
    } finally {
      setLoading(false);
    }
  };

  const handleCloseSnackbar = () => {
    setSnackbarOpen(false);
  };

  const getSoldByPublisherLabel = () => {
    if (isRental) {
      return soldByPublisher ? "Alquilada por mí" : "Alquilada por otra persona";
    } else {
      return soldByPublisher ? "Vendida por mí" : "Vendida por otra persona";
    }
  };

  if (!selectedProperty) {
    return (
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: 200,
          padding: 2,
          backgroundColor: '#f5f5f5',
          borderRadius: 2,
          border: '2px dashed #ccc'
        }}
      >
        <Typography variant="body1" color="text.secondary">
          No hay ninguna propiedad seleccionada
        </Typography>
      </Box>
    );
  }

  return (
    <Box
    sx={{
      border: '1px solid',
      borderColor: 'divider',
      borderRadius: 2,
      backgroundColor: '#fff',
      boxShadow: 1,
      padding: 2,
      margin: 1
    }}
    >
      {/* Header */}
      <Box sx={{ mb: 3 }}>
        <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 1, color: 'green' }}>
          Gestionar Estado de la Propiedad
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Actualiza el estado actual de tu propiedad
        </Typography>
      </Box>

      {/* Información actual */}
      <Paper 
        elevation={1} 
        sx={{ 
          p: 2, 
          mb: 3, 
          backgroundColor: '#f8f9fa',
          borderRadius: 2 
        }}
      >
        <Typography variant="subtitle2" color="text.secondary" gutterBottom>
          Información Actual
        </Typography>
        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center' }}>
          <Typography variant="body2">
            <strong>Tipo:</strong> {selectedProperty.modeDescription}
          </Typography>
          <Typography variant="body2">
            <strong>Estado:</strong> {selectedProperty.statusCode}
          </Typography>
        </Box>
      </Paper>

      {/* Selección de Estado */}
      <Box sx={{ mb: 3 }}>
        <Typography variant="subtitle1" sx={{ fontWeight: 'medium', mb: 2 }}>
          Seleccionar Estado
        </Typography>
        
        <Box sx={{ display: 'flex', gap: 2, mb: 3 }}>
          <Chip
            label={availableLabel}
            icon={<AvailableIcon />}
            onClick={() => handleStateChange(1)}
            color={selectedState === 1 ? "success" : "default"}
            variant={selectedState === 1 ? "filled" : "outlined"}
            sx={{
              fontSize: '14px',
              height: 40,
              cursor: 'pointer',
              transition: 'all 0.2s ease',
              '&:hover': {
                transform: 'translateY(-1px)',
                boxShadow: 2
              }
            }}
          />
          
          <Chip
            label={unavailableLabel}
            icon={<SoldIcon />}
            onClick={() => handleStateChange(isRental ? 3 : 2)}
            color={selectedState === 2 || selectedState === 3 ? "error" : "default"}
            variant={selectedState === 2 || selectedState === 3 ? "filled" : "outlined"}
            sx={{
              fontSize: '14px',
              height: 40,
              cursor: 'pointer',
              transition: 'all 0.2s ease',
              '&:hover': {
                transform: 'translateY(-1px)',
                boxShadow: 2
              }
            }}
          />
        </Box>

        {/* Opción de vendido/alquilado por publisher */}
        <Fade in={selectedState === 2 || selectedState === 3}>
          <Box>
            <Divider sx={{ my: 2 }} />
            <Paper
              elevation={2}
              sx={{
                p: 3,
                borderRadius: 3,
                background: 'linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)',
                border: '1px solid #e0e7ff'
              }}
            >
              <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 'medium' }}>
                ¿Quién realizó la {isRental ? 'renta' : 'venta'}?
              </Typography>
              
              <FormControlLabel
                control={
                  <Switch
                    checked={soldByPublisher}
                    onChange={handleSoldByPublisherChange}
                    color="primary"
                    size="medium"
                  />
                }
                label={
                  <Box
                    sx={{
                      ml: 1,
                      px: 2,
                      py: 1,
                      borderRadius: 2,
                      backgroundColor: soldByPublisher ? '#e3f2fd' : '#fff3e0',
                      border: `1px solid ${soldByPublisher ? '#2196f3' : '#ff9800'}`,
                      boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                      transition: 'all 0.3s ease'
                    }}
                  >
                    <Typography
                      variant="body2"
                      sx={{
                        fontWeight: 'medium',
                        color: soldByPublisher ? '#1976d2' : '#f57c00'
                      }}
                    >
                      {getSoldByPublisherLabel()}
                    </Typography>
                  </Box>
                }
                sx={{ margin: 0, alignItems: 'flex-start' }}
              />
            </Paper>
          </Box>
        </Fade>
      </Box>

      {/* Botón de Guardar */}
      <Box sx={{ display: 'flex', justifyContent: 'center' }}>
        <Button
          variant="contained"
          onClick={handleSave}
          disabled={loading}
          startIcon={loading ? <CircularProgress size={20} /> : <SaveIcon />}
          sx={{
            minWidth: 140,
            height: 44,
            borderRadius: 2,
            textTransform: 'none',
            fontWeight: 'medium',
            boxShadow: 2,
            '&:hover': {
              boxShadow: 4,
              transform: 'translateY(-1px)'
            },
            '&:disabled': {
              backgroundColor: '#f5f5f5'
            }
          }}
        >
          {loading ? 'Guardando...' : 'Guardar Cambios'}
        </Button>
      </Box>

      {/* Snackbar para notificaciones */}
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={4000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity={snackbarSeverity}
          variant="filled"
          sx={{ borderRadius: 2 }}
        >
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </Box>
  );
};